import { ReactNode, createContext, useContext, useState } from 'react'

import { useCallback } from 'modules/core/hooks/useCallback'
import { useEffect } from 'modules/core/hooks/useEffect'

import { UploadFile } from 'modules/expenses/components/common/DropItem'
import { ExpensableExpense } from 'modules/expenses/hooks/useExpensable'
import { expensableResource } from 'modules/expenses/lib/expensables/expensable'
import { Expensable } from 'modules/expenses/types/Expensable'

export interface FilesPreviewerContextValue {
  files: UploadFile[]
  selectedFile: UploadFile | null
  setFilesAfterExpensableUpdate: (updatedExpensable: Expensable) => void
  attachFiles: (files: File[]) => void
  selectFile: (file: UploadFile | null) => void
  removeFile: (file: UploadFile) => void
}

interface FilesPreviewerProviderProps {
  initialFiles: UploadFile[]
  children: ReactNode
}

const FilesPreviewerContext = createContext<FilesPreviewerContextValue>({
  files: [],
  selectedFile: null,
  setFilesAfterExpensableUpdate: () => null,
  attachFiles: () => null,
  selectFile: () => null,
  removeFile: () => null,
})

export const parseExpensableFiles = (expensable: Expensable): UploadFile[] => {
  const resource = expensableResource(expensable)
  if (!resource) return []

  const files = [...resource.files]

  if (expensable.type === 'expense') {
    // eslint-disable-next-line no-type-assertion/no-type-assertion
    const expense = resource as ExpensableExpense
    const signedDocumentFile = expense.signedDocument?.file
    if (signedDocumentFile) files.push(signedDocumentFile)
    if (expense.signedDocument && !signedDocumentFile)
      files.push(...expense.signedDocument?.originalFiles)
  }

  return files.filter(Boolean).map(({ id, filename, url, contentType, byteSize }) => ({
    id: String(id),
    name: filename,
    size: String(byteSize),
    type: contentType ?? '',
    preview: url ?? '',
  }))
}

export const FilesPreviewerProvider = ({ initialFiles, children }: FilesPreviewerProviderProps) => {
  const [files, setFiles] = useState<UploadFile[]>(initialFiles)
  const [selectedFile, setSelectedFile] = useState<UploadFile | null>(null)
  const fileNames = files.map(({ name }) => name).join()

  const setFilesAfterExpensableUpdate = (updatedExpensable: Expensable) => {
    setFiles(parseExpensableFiles(updatedExpensable))
  }

  const attachFiles = (files: Array<File | UploadFile>) => {
    const uploadFiles: UploadFile[] = files.map((file) => ({
      id: file instanceof File ? null : file.id,
      name: file.name,
      type: file.type,
      size: String(file.size),
      preview: file instanceof File ? URL.createObjectURL(file) : file.preview,
    }))

    setFiles((oldFiles) => [...oldFiles, ...uploadFiles])
  }

  const removeFile = (file: UploadFile) => {
    setFiles((files) =>
      files.filter((f) => f.id !== file.id || (f.name !== file.name && f.size !== file.size))
    )
  }

  const selectFile = useCallback(
    (file: UploadFile | null) => {
      if (!file) return setSelectedFile(files[0] ?? null)

      const fileInFiles = files.find(
        (f) => f.id === file.id || (f.name === file.name && f.size === file.size)
      )

      setSelectedFile(fileInFiles ?? null)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fileNames]
  )

  useEffect(() => {
    selectFile(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileNames])

  const value = {
    files,
    setFilesAfterExpensableUpdate,
    selectedFile,
    attachFiles,
    selectFile,
    removeFile,
  }

  return <FilesPreviewerContext.Provider value={value}>{children}</FilesPreviewerContext.Provider>
}

export const useFilesPreviewer = () => useContext(FilesPreviewerContext)
